import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { map, mergeRight } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import {
  useAssociatedOrganizationDelete,
  useOrganizationAssociations,
} from "../organizationServices";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import { fetchOrganization } from "../../util/fetchUtils";
import { useSelector } from "react-redux";
import { capitalize } from "../../util/stringFormat";
import AssociatedOrganizationDialog from "../edit/AssociatedOrganizationDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const orgCache = new Map();
const lookupOrg = (auth, orgId) => {
  if (!orgCache.has(orgId)) {
    orgCache.set(orgId, fetchOrganization(auth)(orgId));
  }

  return orgCache.get(orgId);
};

const OrganizationAssociationsCard = ({ id }) => {
  const auth = useSelector(state => state.auth);
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [associations, setAssociations] = useState([]);

  const associatedResponse = useOrganizationAssociations(id);
  const fetchOrgData = useCallback(
    async assoc => {
      console.log("Fetching Associated Org...");
      const org = await lookupOrg(auth, assoc.associatedOrganizationId);
      console.log(org);

      return mergeRight(assoc, {
        organization: org,
      });
    },
    [auth],
  );

  useEffect(() => {
    if (associatedResponse.isSuccess && notBlank(associatedResponse.data)) {
      Promise.all(map(fetchOrgData, associatedResponse.data))
        .then(data => {
          console.log("Got the associated org...");
          setAssociations(data);
        })
        .catch(err => {
          console.log(err);
        });
    } else {
      setAssociations([]);
    }
  }, [associatedResponse.isSuccess, associatedResponse.data, fetchOrgData]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleAssociatedCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleAssociatedSave = useCallback(
    async data => {
      await associatedResponse.refetch();
      setDialogOpen(false);
    },
    [associatedResponse],
  );

  const assocOrgDelete = useAssociatedOrganizationDelete();
  const handleDeleteClick = useCallback(
    async authAssocOrgId => {
      try {
        await assocOrgDelete.mutateAsync({
          resourceIds: { orgId: id, authAssocOrgId },
        });
        await associatedResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, assocOrgDelete, associatedResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title="Associated Organizations"
        action={
          <Tooltip title="Add Association">
            <IconButton onClick={handleAddClick}>
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {associatedResponse.isError && (
          <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
            Error loading Associated Organizations.
          </Box>
        )}
        {associatedResponse.isLoading && <PanelLoadingIndicator />}
        {associatedResponse.isSuccess && isBlank(associations) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Associated Organization set up for this Organization.
          </Box>
        )}
        {associatedResponse.isSuccess && notBlank(associations) && (
          <List dense>
            {map(
              assoc => (
                <ListItem key={assoc.authenticationAssociatedOrganizationId}>
                  <Tooltip title={assoc.organization.organizationName}>
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={assoc.organization.organizationName}
                      secondary={capitalize(assoc.credentialType)}
                    />
                  </Tooltip>
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() =>
                        handleDeleteClick(
                          assoc.authenticationAssociatedOrganizationId,
                        )
                      }
                      dialogTitle="Remove Association"
                      dialogBody="Are you sure you want to remove this association?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              associations,
            )}
          </List>
        )}
      </CardContent>

      {dialogOpen && (
        <AssociatedOrganizationDialog
          organizationId={id}
          open={dialogOpen}
          onCancel={handleAssociatedCancel}
          onSave={handleAssociatedSave}
        />
      )}
    </Card>
  );
};

export default OrganizationAssociationsCard;
